import React, { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import IndividualAffordabilityPanel from './IndividualAffordabilityPanel';
import { firstPaydayAfter } from '../../pages/admin/IndividualPricingComparison';
import { capturedAffordability } from '../../../services/individualAffordability';
import { individualQuotationContext, QuotationContext, quotationError } from '../../../services/individualPricingQuotations';
import type { ComparisonInput, PaydaySchedule } from '../../../services/individualPricingComparison';
import { pricingVersionLabel } from '../../../utils/pricingVersionLabel';

/** Read-only qualification precedes loan amount capture; no quotations or reports are purchased. */
export default function IndividualQualificationStep({bureauRevision,onReady}: {bureauRevision?: string; onReady?: (ready:boolean)=>void}) {
  const {control,register,setValue} = useFormContext();
  const values = useWatch({control});
  const applicant = values.individual?.idNumber || '';
  const date = values.loan?.commencementDate || '';
  const payday = values.loan?.individualQualificationSettings?.payday || '';
  const missingDay = values.loan?.individualQualificationSettings?.missingDay || 'REJECT';
  const firstDate = firstPaydayAfter(date,String(payday),missingDay);
  const [reload,setReload] = useState(0);
  const contextKey = JSON.stringify({applicant,date,reload});
  const [context,setContext] = useState<{key:string;value?:QuotationContext;error?:string}>();
  useEffect(() => {
    let current = true;
    setContext(undefined);
    if (!applicant || !date) return;
    individualQuotationContext(applicant,date).then(value => {
      if (current) setContext({key:contextKey,value});
    }).catch(failure => {if(current) setContext({key:contextKey,error:quotationError(failure)});});
    return () => {current=false;};
  },[contextKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const current = context?.key === contextKey ? context : undefined;
  const pricing = current?.value;
  const input:ComparisonInput | undefined = pricing && firstDate ? {
    applicantId:applicant,expectedRevision:pricing.pricing.revision,grossAdvance:'8000',advanceDate:date,instalments:1,
    repaymentMethod:pricing.repaymentMethod,classification:pricing.classification,paymentDayTiming:pricing.paymentDayTiming,
    paydaySchedule:{advanceDate:date,firstDueDate:firstDate,payday:Number(payday),instalments:1,
      missingDayRule:missingDay as PaydaySchedule['missingDayRule'],nonWorkingDayRule:'KEEP_CONTRACTUAL_DATE'}
  } : undefined;
  const inputKey = JSON.stringify(input);
  useEffect(() => {setValue('loan.individualQualificationInputs',input);},[inputKey,setValue]); // eslint-disable-line react-hooks/exhaustive-deps
  return <section aria-label="Applicant affordability qualification">
    <h5>Affordability</h5>
    <p>Check how much the applicant qualifies for before choosing the loan amount. These dates determine the repayment schedules.</p>
    <div className="row g-3">
      <div className="col-md-4"><label htmlFor="qualification-date">Planned disbursement date</label>
        <input id="qualification-date" type="date" className="form-control" {...register('loan.commencementDate')} /></div>
      <div className="col-md-4"><label htmlFor="qualification-payday">Monthly payday</label>
        <input id="qualification-payday" type="number" min="1" max="31" step="1" className="form-control" {...register('loan.individualQualificationSettings.payday')} /></div>
      <div className="col-md-4"><label htmlFor="qualification-missing-day">If payday is absent from a month</label>
        <select id="qualification-missing-day" className="form-select" {...register('loan.individualQualificationSettings.missingDay')} defaultValue="REJECT">
          <option value="REJECT">Require a valid date in every month</option><option value="CLAMP_TO_MONTH_END">Use the last calendar day</option>
        </select></div>
    </div>
    {firstDate && <p className="small mt-3">First repayment: {firstDate}. Contractual payday is kept on weekends and holidays.</p>}
    <button type="button" className="btn btn-sm btn-outline-secondary mt-3" disabled={!applicant || !date}
      onClick={()=>setReload(value=>value+1)}>Reload pricing settings</button>
    {pricing && <p className="small mt-3">Pricing settings used in this calculation: {pricingVersionLabel(pricing.pricing)}.</p>}
    {current?.error && <p role="alert">{current.error}</p>}
    <IndividualAffordabilityPanel financial={capturedAffordability(values)} applicantId={applicant} idType={values.individual?.idType} bureauRevision={bureauRevision}
      pricing={input} version={pricing?.pricing.versionId} qualifyByTerm onQualificationReady={onReady} />
  </section>;
}
